import { memo } from "react";

/** A small map legend chip anchored bottom-left of the map canvas. It names
 *  every colour class the map is currently showing in plain language so a
 *  viewer can read the map without a manual. Sharp corners (Palette ban-list),
 *  one accent per state. Click a row to toggle the layer (only when its id
 *  is provided). */

interface LegendRow {
  swatch: string;            // CSS background — coloured dot / line / dot-pair
  swatchType?: "line" | "dot" | "doubleDot" | "dashRect" | "rect";
  label: string;              // short label
  detail?: string;            // secondary line for context
  layerId?: string;           // if present, row is clickable
  active?: boolean;           // visual state when layer off
}

interface Props {
  /** Subset of layer ids currently visible. */
  visibleLayerIds: ReadonlySet<string>;
  rows: LegendRow[];
  onToggle?: (layerId: string) => void;
  title?: string;
}

const swatchStyle = (row: LegendRow): React.CSSProperties => {
  if (row.swatchType === "line") {
    return {
      background: "transparent",
      borderTop: `2px solid ${row.swatch}`,
      width: 16,
      height: 0,
      opacity: row.active ? 0.35 : 1,
    };
  }
  if (row.swatchType === "dashRect") {
    return {
      background: row.swatch,
      border: "1px dashed rgba(0,0,0,0.5)",
      width: 14,
      height: 10,
      opacity: row.active ? 0.35 : 1,
    };
  }
  if (row.swatchType === "rect") {
    return {
      background: row.swatch,
      width: 14,
      height: 10,
      opacity: row.active ? 0.35 : 1,
    };
  }
  if (row.swatchType === "doubleDot") {
    return {
      background: `radial-gradient(circle, ${row.swatch} 0 40%, transparent 41%) 0 50%/8px 8px no-repeat,
                   radial-gradient(circle, ${row.swatch} 0 40%, transparent 41%) 8px 50%/8px 8px no-repeat`,
      width: 16,
      height: 8,
      opacity: row.active ? 0.35 : 1,
    };
  }
  // default: dot
  return {
    background: row.swatch,
    width: 10,
    height: 10,
    borderRadius: 0,
    opacity: row.active ? 0.35 : 1,
  };
};

export const MapLegend = memo(function MapLegend({
  visibleLayerIds,
  rows,
  onToggle,
  title = "MAP LEGEND",
}: Props) {
  return (
    <aside className="map-legend" aria-label="Map legend">
      <div className="map-legend__head">
        <span className="mono eyebrow">{title}</span>
      </div>
      <ul className="map-legend__list">
        {rows.map((row) => {
          const visible = !row.layerId || visibleLayerIds.has(row.layerId);
          const interactive = Boolean(row.layerId && onToggle);
          return (
            <li
              key={row.label}
              className={
                "map-legend__row" +
                (interactive ? " map-legend__row--clickable" : "") +
                (!visible ? " map-legend__row--off" : "")
              }
              onClick={interactive ? () => onToggle!(row.layerId!) : undefined}
              role={interactive ? "button" : undefined}
              tabIndex={interactive ? 0 : undefined}
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onToggle!(row.layerId!);
                      }
                    }
                  : undefined
              }
              title={
                interactive
                  ? visible
                    ? `Click to hide ${row.label}`
                    : `Click to show ${row.label}`
                  : row.detail
              }
            >
              <span
                className="map-legend__swatch"
                style={swatchStyle(row)}
                aria-hidden
              />
              <span className="map-legend__text">
                <span className="map-legend__label">{row.label}</span>
                {row.detail && (
                  <span className="map-legend__detail">{row.detail}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="map-legend__attr caption mono">
        Channels: OpenStreetMap (ODbL). Gauges: HII via FloodDash.
      </p>
    </aside>
  );
});

/** A readymade legend for the waterways + flood-gauge layers that ship in
 *  KMITL's default lenses. Every entry corresponds to a real source: the
 *  channel lines are verbatim OpenStreetMap geometry, and the gauge row is
 *  the live HII situation-level scale. */
export const WaterwayLegend = memo(function WaterwayLegend({
  visible,
}: {
  visible: boolean;
}) {
  if (!visible) return null;
  return (
    <aside className="map-legend" aria-label="Waterway legend">
      <div className="map-legend__head">
        <span className="mono eyebrow">WATERWAY</span>
      </div>
      <ul className="map-legend__list">
        <li className="map-legend__row">
          <span
            className="map-legend__swatch"
            style={{ background: "var(--map-canal)", width: 14, height: 4 }}
            aria-hidden
          />
          <span className="map-legend__text">
            <span className="map-legend__label">Canal</span>
            <span className="map-legend__detail">Cerulian Blue · flows to river</span>
          </span>
        </li>
        <li className="map-legend__row">
          <span
            className="map-legend__swatch"
            style={{ background: "var(--map-drain)", width: 14, height: 2 }}
            aria-hidden
          />
          <span className="map-legend__text">
            <span className="map-legend__label">Storm drain</span>
            <span className="map-legend__detail">Benzol Green · urban runoff</span>
          </span>
        </li>
        <li className="map-legend__row">
          <span
            className="map-legend__swatch"
            style={{
              background:
                "linear-gradient(90deg, var(--gauge-safe) 0 25%, var(--gauge-watch) 25% 50%, var(--gauge-prepare) 50% 75%, var(--gauge-evacuate) 75% 100%)",
              width: 14,
              height: 6,
            }}
            aria-hidden
          />
          <span className="map-legend__text">
            <span className="map-legend__label">HII gauge · live</span>
            <span className="map-legend__detail">L1–2 safe · L3 watch · L4 prepare · L5 over bank</span>
          </span>
        </li>
        <li className="map-legend__row">
          <span
            className="map-legend__swatch"
            style={{
              background: "var(--gauge-stale)",
              border: "1.5px solid var(--gauge-stale-ring)",
              borderRadius: "50%",
              width: 11,
              height: 11,
            }}
            aria-hidden
          />
          <span className="map-legend__text">
            <span className="map-legend__label">Gauge · not reporting</span>
            <span className="map-legend__detail">last reading is days old — history, not an alert</span>
          </span>
        </li>
        <li className="map-legend__row">
          <span
            className="map-legend__swatch"
            style={{
              background: "var(--map-canal)",
              width: 0,
              height: 0,
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderBottom: "8px solid var(--map-canal)",
              transform: "rotate(90deg)",
              marginLeft: 2,
            }}
            aria-hidden
          />
          <span className="map-legend__text">
            <span className="map-legend__label">Flow direction</span>
            <span className="map-legend__detail">upstream → downstream</span>
          </span>
        </li>
      </ul>
      <p className="map-legend__attr caption mono">
        Channels: OpenStreetMap (ODbL). Gauges: HII via FloodDash.
      </p>
    </aside>
  );
});